import React, { useState } from 'react';

const SchedulingModal = ({ session, projectId, content, contentType, onScheduled, onClose, showToast }) => {
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const notify = (msg, type) => {
    if (showToast) showToast(msg, type);
  };

  const stripHtml = (html) => html.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

  const getFirstTweet = () => {
    const plain = content ? stripHtml(content) : '';
    return plain.split('---')[0].replace(/^\d+\/\s*/, '').trim().substring(0, 280);
  };

  const openXWithTweet = () => {
    const firstTweet = getFirstTweet();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(firstTweet)}`, '_blank', 'noopener,noreferrer');
  };

  const handleSchedule = () => {
    if (!session || !content) return;

    if (scheduleType === 'now') {
      openXWithTweet();
      notify('First tweet pre-filled on X — post it, then add the rest as replies!', 'success');
      if (onScheduled) onScheduled({ message: 'Opened X with your thread!' });
      return;
    }

    if (scheduleType === 'optimal') {
      openXWithTweet();
      notify('First tweet pre-filled on X — use X\'s built-in scheduler to set the optimal time!', 'success');
      if (onScheduled) onScheduled({ message: 'Opened X with your thread!' });
      return;
    }

    if (scheduleType === 'custom') {
      if (!scheduledDate || !scheduledTime) {
        notify('Please select a date and time', 'error');
        return;
      }
      openXWithTweet();
      notify('First tweet pre-filled on X — use X\'s built-in scheduler to set your chosen time!', 'success');
      if (onScheduled) onScheduled({ message: 'Opened X with your thread!' });
    }
  };

  const formatOptimalTime = () => {
    const hour = new Date().getHours();
    if (hour >= 9 && hour < 12) return "Today at 3:00 PM (peak engagement)";
    if (hour >= 12 && hour < 18) return "Tomorrow at 9:00 AM (morning surge)";
    return "Tomorrow at 3:00 PM (peak engagement)";
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(8px)',
      animation: 'fadeIn 0.2s ease', padding: '20px'
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: '20px', padding: '32px', width: '100%',
        maxWidth: '550px', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-color)',
        animation: 'modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
      }} onClick={e => e.stopPropagation()}>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📅</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
            Schedule Thread
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Open X with your first tweet pre-filled
          </p>
        </div>

        {/* Schedule Type */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block', fontSize: '14px', fontWeight: '600',
            color: 'var(--text-primary)', marginBottom: '10px'
          }}>
            When to post
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { id: 'now', name: 'Post Now', desc: 'Opens X with your first tweet ready to post', icon: '⚡' },
              { id: 'optimal', name: 'Optimal Time', desc: formatOptimalTime(), icon: '🎯' },
              { id: 'custom', name: 'Custom Time', desc: 'Choose specific date & time', icon: '🗓️' }
            ].map(option => (
              <button
                key={option.id}
                onClick={() => setScheduleType(option.id)}
                style={{
                  padding: '16px', borderRadius: '12px', textAlign: 'left',
                  border: `2px solid ${scheduleType === option.id ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                  background: scheduleType === option.id ? 'var(--accent-subtle-bg)' : 'var(--bg-secondary)',
                  cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '14px',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '28px' }}>{option.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '15px', fontWeight: '600',
                    color: scheduleType === option.id ? 'var(--accent-primary)' : 'var(--text-primary)',
                    marginBottom: '4px'
                  }}>
                    {option.name}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {option.desc}
                  </div>
                </div>
                {scheduleType === option.id && (
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: 'var(--accent-primary)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', color: 'white',
                    fontSize: '12px', fontWeight: '700'
                  }}>✓</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Custom DateTime Picker */}
        {scheduleType === 'custom' && (
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block', fontSize: '14px', fontWeight: '600',
              color: 'var(--text-primary)', marginBottom: '10px'
            }}>
              Select Date & Time
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <input
                type="date"
                value={scheduledDate}
                onChange={e => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{
                  padding: '12px 14px', borderRadius: '10px',
                  border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'inherit', outline: 'none'
                }}
              />
              <input
                type="time"
                value={scheduledTime}
                onChange={e => setScheduledTime(e.target.value)}
                style={{
                  padding: '12px 14px', borderRadius: '10px',
                  border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'inherit', outline: 'none'
                }}
              />
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
              Use X's built-in scheduler to set this time after your tweet is pre-filled
            </div>
          </div>
        )}

        {/* Info Box */}
        <div style={{
          padding: '16px', borderRadius: '10px', marginBottom: '24px',
          background: 'var(--accent-subtle-bg)', border: '1px solid var(--accent-subtle-border)'
        }}>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
            💡 <strong style={{ color: 'var(--text-primary)' }}>How it works:</strong>{' '}
            Your first tweet will be pre-filled in X's composer. Post it, then paste the remaining tweets as replies to build your thread.
            {scheduleType !== 'now' && ' Use X\'s schedule button (calendar icon) to set your posting time.'}
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '14px', borderRadius: '10px',
              border: '2px solid var(--border-color)', background: 'transparent',
              color: 'var(--text-secondary)', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={scheduleType === 'custom' && (!scheduledDate || !scheduledTime)}
            style={{
              flex: 2, padding: '14px', borderRadius: '10px', border: 'none',
              background: 'var(--accent-gradient)', color: 'white',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
              opacity: (scheduleType === 'custom' && (!scheduledDate || !scheduledTime)) ? 0.5 : 1
            }}
          >
            𝕏 Schedule on X
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchedulingModal;
